import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from './api'
import { DateTime } from 'luxon'
import { formatDateOnly, formatTime, timePattern } from './format'
import { reportSchedule } from './types'

/**
 * Per-tenant Report Schedule, fetched from the same endpoint the
 * Company Admin's Report Schedule page writes to. Used by the user-
 * side Daily / Weekly / Monthly report pages so the period boundary
 * and the submit-by deadline come from one source of truth — the
 * admin's config — instead of being hardcoded inside each report
 * page.
 *
 * Cached at `['report-schedule']` to match the admin page's
 * `queryKey` so a save on the admin page invalidates the user-side
 * pages automatically.
 */
export function useReportSchedule() {
  const query = useQuery({
    queryKey: ['report-schedule'],
    queryFn: async () => {
      const res = await api.get('/reports/config/schedule')
      return reportSchedule.parse(res.data)
    },
    // The schedule rarely changes — 5 min staleTime keeps the user
    // pages snappy without sacrificing freshness when an admin
    // updates the config in another tab.
    staleTime: 5 * 60 * 1000,
  })

  const helpers = useMemo(
    () => buildHelpers(query.data ?? null),
    [query.data],
  )

  return {
    schedule: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    ...helpers,
  }
}

/** Build helper closures around a schedule snapshot. Extracted so
 *  the same shape (`{ weekBoundsFor, monthBoundsFor, ... }`) is
 *  returned whether the query is loading or not — pages can call
 *  these without null-checking, they just fall back to safe defaults
 *  while the fetch is in flight. */
function buildHelpers(schedule) {
  const dailyActiveDays = schedule?.dailyActiveDays ?? [1, 2, 3, 4, 5]
  const weeklyDayOfWeek = schedule?.weeklyDayOfWeek ?? 5 // Fri
  const monthlyDayOfMonth = schedule?.monthlyDayOfMonth ?? 28
  // Which month a fresh monthly report defaults to: `previous` = the
  // just-closed month ("submit last month"), `current` = the month in
  // progress. Drives only the DEFAULT period the page pre-selects — the
  // window itself is always a whole calendar month.
  const monthlyScope = schedule?.monthlyScope ?? 'current'
  const dailyReminderAt = schedule?.dailyReminderAt ?? '17:30'
  const weeklyReminderAt = schedule?.weeklyReminderAt ?? '16:00'
  const monthlyReminderAt = schedule?.monthlyReminderAt ?? '15:00'

  function isDailyActiveDay(date) {
    return dailyActiveDays.includes(toDate(date).getDay())
  }

  /** Window the WEEKLY report covers, given any anchor date inside it:
   *  the WORKING WEEK — first → last active weekday (`dailyActiveDays`)
   *  inside the company week containing `date`. The company week starts
   *  on Monday unless the working week wraps over Sunday (Sun–Thu,
   *  Sat–Wed), in which case it starts on the first working day — the
   *  same rule as the API's `workingWeekStartDay`, so both sides agree.
   *  With the default Mon–Fri active days this is Monday → Friday. The
   *  weekly *deadline* is a separate thing and lands on the admin's
   *  `weeklyDayOfWeek` — see `weeklyDeadlineFor`. */
  function weekBoundsFor(date) {
    const startDow = workingWeekStartDay(dailyActiveDays)
    const anchor = startOfDay(toDate(date))
    const weekStart =
      startDow === 1 ? mondayOf(anchor) : addDays(anchor, -((anchor.getDay() - startDow + 7) % 7))
    const matched = []
    for (let i = 0; i < 7; i += 1) {
      const d = addDays(weekStart, i)
      if (dailyActiveDays.includes(d.getDay())) matched.push(d)
    }
    const start = matched.length ? matched[0] : weekStart
    const end = matched.length ? matched[matched.length - 1] : addDays(weekStart, 6)
    return { start: startOfDay(start), end: startOfDay(end) }
  }

  /** Window the MONTHLY report covers, given any anchor date inside it:
   *  the WHOLE calendar month (1st → last day) containing `date`. The
   *  `monthlyScope` (this month vs the just-closed month) only decides
   *  which month a fresh report defaults to — that lives at the page
   *  level; this helper always returns a full month for whatever month
   *  `date` falls in. */
  function monthBoundsFor(date) {
    const d = toDate(date)
    const start = new Date(d.getFullYear(), d.getMonth(), 1)
    const end = new Date(d.getFullYear(), d.getMonth() + 1, 0) // last day of month
    return { start: startOfDay(start), end: startOfDay(end) }
  }

  /** Submit-by Date the DAILY report for `date` is due. The deadline
   *  sits at `dailyReminderAt` ON the day being reported. */
  function dailyDeadlineFor(date) {
    return stampTime(toDate(date), dailyReminderAt)
  }

  /** Submit-by Date the WEEKLY report whose window ENDS on `weekEnd` is
   *  due. The deadline is the admin's `weeklyDayOfWeek` at
   *  `weeklyReminderAt` — the first such weekday ON or AFTER the period
   *  end. That mirrors the backend reminder, which fires on
   *  `weeklyDayOfWeek` once the working week has closed
   *  (`jobs/reminders.runner.js`).
   *
   *  This used to stamp the time onto `weekEnd` itself and ignore
   *  `weeklyDayOfWeek` entirely, so changing "Day of Week" on the Report
   *  Schedule page never reached this chip. The bug hid because the
   *  default day (5 = Fri) happens to equal the default working-week end
   *  (Mon–Fri active days) — so only tenants who changed the day saw it.
   *  With the default the result is unchanged: the next Fri on/after a
   *  Fri period-end is that same Friday. */
  function weeklyDeadlineFor(weekEnd) {
    const end = startOfDay(toDate(weekEnd))
    const daysAhead = (weeklyDayOfWeek - end.getDay() + 7) % 7
    return stampTime(addDays(end, daysAhead), weeklyReminderAt)
  }

  /** Submit-by Date the MONTHLY report whose window ENDS on `monthEnd`
   *  is due. The deadline lands on the admin's `monthlyDayOfMonth` at
   *  `monthlyReminderAt`, in the month the report is actually FILED —
   *  which depends on `monthlyScope`:
   *    - `previous` (submit last month's report) → day N of the month
   *      AFTER the period (e.g. July's report is due day 1 of August).
   *    - `current`  (submit this month's report) → day N of the period's
   *      OWN month (e.g. day 28 of July for July's report).
   *  This mirrors the backend reminder, which fires when today is
   *  `monthlyDayOfMonth` (`jobs/reminders.runner.js`). The old version
   *  stamped the time onto the month-end day and ignored BOTH
   *  `monthlyDayOfMonth` and `monthlyScope`, so the "Submit by …" date
   *  and the Overdue flip were both wrong for any tenant not filing on
   *  the last day of the period month. QA 2026-08-31. */
  function monthlyDeadlineFor(monthEnd) {
    const end = toDate(monthEnd)
    const target = new Date(end.getFullYear(), end.getMonth(), 1)
    if (monthlyScope !== 'current') target.setMonth(target.getMonth() + 1)
    // Clamp the day to the target month's length so day 31 in a 30-day
    // month (or Feb) lands on the last day, never spills into next month.
    const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate()
    target.setDate(Math.min(monthlyDayOfMonth, lastDay))
    return stampTime(target, monthlyReminderAt)
  }

  return {
    isDailyActiveDay,
    weekBoundsFor,
    monthBoundsFor,
    dailyDeadlineFor,
    weeklyDeadlineFor,
    monthlyDeadlineFor,
    // Re-export the raw schedule scalars so pages can render labels
    // like "Submit by 5:30 PM" without re-deriving them.
    dailyActiveDays,
    weeklyDayOfWeek,
    monthlyDayOfMonth,
    monthlyScope,
    dailyReminderAt,
    weeklyReminderAt,
    monthlyReminderAt,
    dailyEnabled: schedule?.dailyEnabled ?? true,
    weeklyEnabled: schedule?.weeklyEnabled ?? true,
    monthlyEnabled: schedule?.monthlyEnabled ?? true,
  }
}

// ── Small date helpers — local-time only ────────────────────────

function toDate(v) {
  if (v instanceof Date) return v
  if (typeof v === 'string') return new Date(v)
  return new Date(v)
}

function startOfDay(d) {
  const x = new Date(d)
  x.setHours(0, 0, 0, 0)
  return x
}

function addDays(d, n) {
  const x = new Date(d)
  x.setDate(x.getDate() + n)
  return x
}

/**
 * The weekday (0=Sun..6=Sat) the COMPANY's week starts on, from its working
 * days. MIRRORS flowdesk-api/src/utils/report-period.js `workingWeekStartDay`:
 * Monday, unless the working week wraps over Sunday→Monday (both working, at
 * least one day off) — then the first working day after the days off.
 */
export function workingWeekStartDay(activeDays) {
  const days = new Set(Array.isArray(activeDays) && activeDays.length ? activeDays : [1, 2, 3, 4, 5])
  if (days.size >= 7 || !days.has(0) || !days.has(1)) return 1
  let start = 0
  while (days.has((start + 6) % 7) && (start + 6) % 7 !== 1) start = (start + 6) % 7
  return start
}

/** Monday (00:00 local) of the week containing `d`. Sunday rolls back
 *  to the previous Monday so a Mon-anchored week stays whole. */
function mondayOf(d) {
  const x = startOfDay(d)
  const day = x.getDay() // 0=Sun..6=Sat
  const delta = day === 0 ? -6 : 1 - day
  x.setDate(x.getDate() + delta)
  return x
}

/** Build a Date at `HH:MM` on the same calendar day as `base`. */
function stampTime(base, hhmm) {
  const [h, m] = String(hhmm ?? '00:00')
    .split(':')
    .map((s) => Number.parseInt(s, 10) || 0)
  const x = new Date(base)
  x.setHours(h, m, 0, 0)
  return x
}

/**
 * Format a Date / time-of-day string as "5:30 PM" / "12:00 AM".
 *
 * TIME-ONLY by design — the only callers are the reminder summary
 * lines on `ReportSchedulePage` (and the matching "Submit by ..."
 * badges on `ReportsPage`) where the date is implicit in the cadence
 * sentence ("Submit by 5:30 PM", "Submit by 4:00 PM Fri", "Submit by
 * 3:00 PM on day 28"). Adding a date here would be redundant and
 * wrong, which is why this helper is isolated and named to make the
 * narrow intent obvious.
 *
 * For anything that needs a full `DD/MM/YYYY HH:MM AM/PM` timestamp,
 * use the canonical `formatDateTime` from `./format` instead — do
 * NOT extend this helper to add a date component.
 *
 * Follows the person's 12/24-hour clock. A "HH:mm" schedule string is a
 * wall-clock setting, so it is printed as-is (no zone shift); a Date /
 * ISO instant renders in the person's display timezone.
 */
export function formatTimeOnly(value) {
  if (typeof value === 'string' && /^\d{1,2}:\d{2}$/.test(value)) {
    const [hour, minute] = value.split(':').map((s) => Number.parseInt(s, 10))
    const dt = DateTime.fromObject({ hour, minute }, { locale: 'en-GB' })
    if (!dt.isValid) return value
    return dt.toFormat(timePattern()).replace(/\b(am|pm)\b/i, (m) => m.toUpperCase())
  }
  return formatTime(value)
}

/**
 * Format a period range label — e.g. the "01/05/2026 – 28/05/2026"
 * captions on the weekly / monthly report pages.
 *
 * Period labels are one of the few date-only buckets in the app (the
 * exact time of day inside the period is meaningless), so this stays
 * date-only and delegates to the canonical `formatDateOnly` helper to
 * keep the `DD/MM/YYYY` spec consistent across the codebase. Callers
 * that need the full `DD/MM/YYYY HH:MM AM/PM` timestamp should use
 * `formatDateTime` from `./format` instead.
 */
export function formatPeriodDate(value) {
  return formatDateOnly(value)
}

/** "01/08/2026" (in the person's date format) for a deadline / period Date
 *  built by this module's browser-local calendar math. The local Y-M-D is
 *  taken as the calendar day it names — passing the Date itself to
 *  `formatDate` would re-read the local-midnight instant in the company
 *  zone and could name the day before or after. */
export function formatLocalCalendarDate(value) {
  const d = value instanceof Date ? value : toDate(value)
  if (!d || Number.isNaN(d.getTime())) return '—'
  const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  return formatDateOnly(key)
}

/** "Mon" / "Fri" — used for the deadline label "Submit by 4 PM Fri".
 *  Deliberately browser-local: the deadline it names is built by local
 *  calendar math (`weeklyDeadlineFor`), so shifting it into another zone
 *  could name the wrong day. A weekday name has no format preference. */
export function formatWeekdayShort(value) {
  const d = value instanceof Date ? value : new Date(value)
  return d.toLocaleDateString('en-GB', { weekday: 'short' })
}
